/**
 * AUTH57 auth / transport failure classification for bootstrap and offline foundation.
 *
 * AUTH_FAILURE — clear session; wipe private offline namespace when memberId known
 * TRANSPORT_FAILURE — keep token/identity; may enter offline_cached when identity cached
 * AUTHORITATIVE_DENIAL — hard business deny; do not treat as offline auth success
 */

export const AUTH_FAILURE_CLASS = Object.freeze({
  AUTH_FAILURE: 'AUTH_FAILURE',
  TRANSPORT_FAILURE: 'TRANSPORT_FAILURE',
  AUTHORITATIVE_DENIAL: 'AUTHORITATIVE_DENIAL',
});

const TOKEN_EXPIRED_CODE = 90005;
const TOKEN_NOT_FOUND_CODE = 90003;
const TOKEN_ILLEGAL_CODE = 90006;

function readCode(error) {
  if (error == null) return null;
  if (typeof error.code === 'number') return error.code;
  if (typeof error.raw?.code === 'number') return error.raw.code;
  return null;
}

function readKey(error) {
  const key = error?.raw?.key ?? error?.key;
  return key == null ? '' : String(key).toLowerCase();
}

function readHttpStatus(error) {
  if (typeof error?.httpStatus === 'number') return error.httpStatus;
  if (typeof error?.response?.status === 'number') return error.response.status;
  if (typeof error?.raw?.httpStatus === 'number') return error.raw.httpStatus;
  return null;
}

function readNetworkCode(error) {
  if (error?.networkCode) return String(error.networkCode);
  if (error?.code && typeof error.code === 'string') return error.code;
  return '';
}

function isTokenAuthFailure(error) {
  const code = readCode(error);
  const key = readKey(error);
  if (code === TOKEN_EXPIRED_CODE || code === TOKEN_NOT_FOUND_CODE || code === TOKEN_ILLEGAL_CODE) {
    return true;
  }
  if (key === 'token.expired' || key.includes('token.not.found') || key.includes('token.illegal')) {
    return true;
  }
  return false;
}

/**
 * Classify errors for AuthContext getInfo bootstrap (and similar auth validation).
 * Prefer structured fields (business code / httpStatus / axios network codes) over message text.
 */
export function classifyAuthBootstrapFailure(error) {
  if (error == null) {
    return AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
  }

  if (isTokenAuthFailure(error)) {
    return AUTH_FAILURE_CLASS.AUTH_FAILURE;
  }

  const httpStatus = readHttpStatus(error);
  if (httpStatus === 401) {
    return AUTH_FAILURE_CLASS.AUTH_FAILURE;
  }
  if (httpStatus === 403) {
    return AUTH_FAILURE_CLASS.AUTHORITATIVE_DENIAL;
  }
  if (httpStatus === 404) {
    return AUTH_FAILURE_CLASS.AUTHORITATIVE_DENIAL;
  }
  if (httpStatus != null && httpStatus >= 500) {
    return AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
  }

  const networkCode = readNetworkCode(error);
  if (
    networkCode === 'ECONNABORTED'
    || networkCode === 'ERR_NETWORK'
    || networkCode === 'ETIMEDOUT'
    || networkCode === 'ECONNREFUSED'
  ) {
    return AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
  }

  // Axios / ApiError with no HTTP response ⇒ transport (offline, DNS, CORS network fail)
  if (httpStatus == null && readCode(error) == null) {
    return AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
  }

  // Known Travel authoritative business denials (rare on getInfo; keep classified)
  const code = readCode(error);
  if (code === 92001 || code === 92002) {
    return AUTH_FAILURE_CLASS.AUTHORITATIVE_DENIAL;
  }

  // Structured business failure that is not transport ⇒ do not unlock offline_cached
  if (code != null && code !== 200) {
    return AUTH_FAILURE_CLASS.AUTH_FAILURE;
  }

  return AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
}

export function isTransportFailure(error) {
  return classifyAuthBootstrapFailure(error) === AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
}

export function isAuthFailure(error) {
  return classifyAuthBootstrapFailure(error) === AUTH_FAILURE_CLASS.AUTH_FAILURE;
}
